<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Clue } from '@/types'
import ClueRow from './ClueRow.vue'
import type { Timestamp } from 'firebase/firestore'

const props = defineProps<{
  clues: Clue[]
  incidentId: string
}>()

type SortKey = 'found_at' | 'status'
const sortKey = ref<SortKey>('found_at')
const sortDir = ref<'asc' | 'desc'>('desc')

const statusOrder: Record<string, number> = { unconfirmed: 0, confirmed: 1, resolved: 2 }

function toMs(ts: unknown): number {
  if (!ts) return 0
  const t = ts as Timestamp
  return t.toMillis ? t.toMillis() : 0
}

const sorted = computed(() => {
  return [...props.clues].sort((a, b) => {
    let cmp = 0
    if (sortKey.value === 'found_at') {
      cmp = toMs(a.found_at) - toMs(b.found_at)
    } else {
      cmp = (statusOrder[a.status] ?? 0) - (statusOrder[b.status] ?? 0)
    }
    return sortDir.value === 'asc' ? cmp : -cmp
  })
})

function toggleSort(key: SortKey) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = key === 'found_at' ? 'desc' : 'asc'
  }
}

function sortIcon(key: SortKey) {
  if (sortKey.value !== key) return 'pi pi-sort'
  return sortDir.value === 'asc' ? 'pi pi-sort-up' : 'pi pi-sort-down'
}
</script>

<template>
  <div class="overflow-x-auto rounded-lg border border-border">
    <table class="min-w-full">
      <thead class="bg-surface-container border-b border-border">
        <tr>
          <th class="px-4 py-3 text-left text-xs font-semibold text-outline uppercase tracking-wide w-10">#</th>
          <th class="px-4 py-3 text-left text-xs font-semibold text-outline uppercase tracking-wide">Description</th>
          <th class="px-4 py-3 text-left text-xs font-semibold text-outline uppercase tracking-wide">Coordinates</th>
          <th class="px-4 py-3 text-left text-xs font-semibold text-outline uppercase tracking-wide">Found By</th>
          <th
            class="px-4 py-3 text-left text-xs font-semibold text-outline uppercase tracking-wide cursor-pointer select-none whitespace-nowrap"
            @click="toggleSort('found_at')"
          >
            Time Found
            <i :class="[sortIcon('found_at'), 'ml-1 text-outline-variant']" />
          </th>
          <th
            class="px-4 py-3 text-left text-xs font-semibold text-outline uppercase tracking-wide cursor-pointer select-none"
            @click="toggleSort('status')"
          >
            Status
            <i :class="[sortIcon('status'), 'ml-1 text-outline-variant']" />
          </th>
          <th class="px-4 py-3 text-left text-xs font-semibold text-outline uppercase tracking-wide">Actions</th>
        </tr>
      </thead>
      <tbody class="bg-surface">
        <ClueRow
          v-for="(clue, i) in sorted"
          :key="clue.clue_id"
          :clue="clue"
          :index="i"
          :incident-id="incidentId"
        />
        <tr v-if="clues.length === 0">
          <td colspan="7" class="px-4 py-12 text-center text-slate-400 dark:text-slate-500 text-sm">
            No clues logged yet.
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
